// ─────────────────────────────────────────────────────────────────────
// ConfiguratorWizardPage — /configurer (wizard v2)
// Flow: Format → Pays/Ville → Chantiers → Contact + Liste d'attente
// DA Sabril · Hermès · ivory / vert profond / or · Playfair éditorial
// ─────────────────────────────────────────────────────────────────────

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Radio, Plane, Sparkles, Check, MapPin, Mail, Phone, Store, User, MessageSquare, Calendar, ArrowRight, Loader2, Download, MessageCircle } from 'lucide-react';
import { DA, MimosaBranch } from '../theme/sabril-da';
import SalonPriveGate, { isSalonPriveUnlocked } from '../components/SalonPriveGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SABRIL_WHATSAPP = '33745522450'; // Sabril · WhatsApp direct
const fmt = (n) => new Intl.NumberFormat('fr-FR').format(n);

// ─── Pricing constants ──────────────────────────────────────────────
const FORMAT_BASE_PRICE = {
  distanciel: 4990,
  audit48:    2790,
  hybride:    24990, // tout inclus · les 12 chantiers offerts
};
const HYBRIDE_ALL_INCLUDED = true; // Hybride = 12 chantiers verrouillés inclus

// ─── Formats — 3 cartes claires avec prix visibles ──────────────────
// Ordre : 48h Express (1er) → 100% Distance → Hybride 90 jours
const FORMATS = [
  {
    key: 'audit48',
    icon: Plane,
    label: '48h express sur site',
    duration: '2 jours immersion',
    priceLabel: '2 790 € HT · paiement unique',
    priceSub: 'Vol/train + hôtel à votre charge',
    tagline: 'Je débarque chez vous 2 jours. Immersion totale, plan d\u2019action 30 jours livré au départ.',
    highlight: 'Résultats immédiats',
    included: [
      'J1 · Arrivée 14h → audit terrain jusqu\u2019à 17h',
      'J1 · Repas de travail 20h avec le dirigeant',
      'J2 · 10h — restitution + plan d\u2019action 30 jours',
      'Suivi WhatsApp direct pendant 30 jours',
      'Livrable : plan d\u2019action détaillé à exécuter',
    ],
  },
  {
    key: 'distanciel',
    icon: Radio,
    label: '100 % à distance',
    duration: '90 jours',
    priceLabel: 'À partir de 4 990 € HT',
    priceSub: '+ chantiers choisis',
    tagline: 'On travaille ensemble à distance pendant 3 mois — vous gardez votre resto ouvert, moi je vous coache chaque semaine.',
    highlight: 'Le plus flexible',
    included: [
      '3 mois de coaching · 1h30 en visio tous les 15 jours',
      'WhatsApp direct · réponse sous 4h',
      'Feuille de route 90 jours chiffrée',
      'Bilan mensuel · ajustements',
    ],
  },
  {
    key: 'hybride',
    icon: Sparkles,
    label: 'Accompagnement 90 jours',
    duration: '15j terrain + 75j suivi',
    priceLabel: '24 990 € HT',
    priceSub: 'Tout inclus · les 12 chantiers',
    tagline: 'Le format signature — 15 jours de présence terrain + 75 jours de suivi rapproché. Les 12 chantiers inclus.',
    highlight: 'Le plus complet',
    included: [
      '15 jours terrain · immersion totale',
      '75 jours de suivi WhatsApp + visio',
      'Les 12 chantiers inclus',
      'Vol + hébergement pris en charge',
    ],
  },
];

// ─── Pays disponibles ────────────────────────────────────────────────
const COUNTRIES = [
  { code: 'FR', flag: '🇫🇷', label: 'France' },
  { code: 'ES', flag: '🇪🇸', label: 'Espagne' },
  { code: 'MA', flag: '🇲🇦', label: 'Maroc' },
  { code: 'EU', flag: '🇪🇺', label: 'Europe (autre)' },
];

// ─── 12 chantiers cochables — alignés avec la landing /prsnl ─────────
// Chaque chantier = 5 bullets en "On…" (action, pas de mots de consultant).
// Chantiers 00 & 01 = FONDATIONS obligatoires dès qu'un autre chantier est ajouté.
const FOUNDATION_KEYS = ['00', '01'];
const CHANTIERS = [
  { n: '00', title: 'Coaching dirigeant · 12h', price: 2990, foundation: true, bullets: [
    'On vous coache pendant 12h en individuel.',
    'On construit votre plan stratégique sur 90 jours.',
    'On met en place votre journal de bord dirigeant.',
    'On audite votre resto de A à Z, chiffres inclus.',
    'On identifie où l\u2019argent fuit chaque mois.',
  ] },
  { n: '01', title: 'ADN & histoire de marque', price: 1990, foundation: true, bullets: [
    'On définit les clients qui dépenseront le plus chez vous.',
    'On crée un concept impossible à confondre avec un concurrent.',
    'On écrit votre histoire et votre slogan.',
    'On trouve ce qui vous permettra de vendre plus cher.',
    'On construit votre identité pour les 10 prochaines années.',
  ] },
  { n: '02', title: 'Expérience au sein du lieu', price: 2490, bullets: [
    'On imagine le parcours idéal du client, minute par minute.',
    'On crée vos rituels de service signature.',
    'On règle l\u2019ambiance sonore selon les moments.',
    'On travaille l\u2019éclairage et les odeurs.',
    'On écrit les 10 règles d\u2019or de votre équipe en salle.',
  ] },
  { n: '03', title: 'Design d\u2019espace', price: 2490, bullets: [
    'On crée votre moodboard et on valide la direction en 7 jours.',
    'On dessine une signature visuelle iconique pour votre lieu.',
    'On livre le plan 3D et on organise les flux de service.',
    'On conçoit toute la signalétique interne.',
    'On règle l\u2019ambiance sensorielle du lieu.',
  ] },
  { n: '04', title: 'Menu Gagnant', price: 1990, bullets: [
    'On refond votre carte plat par plat.',
    'On sélectionne vos 6 à 8 signatures rentables.',
    'On calcule les marges de chaque plat.',
    'On donne un nom iconique à vos plats phares.',
    'On organise le shooting photo pro de la carte.',
  ] },
  { n: '05', title: 'Fiches techniques · Food cost & marges', price: 1990, bullets: [
    'On calcule vos vrais coûts matière au centime.',
    'On standardise chaque recette de votre carte.',
    'On met en place vos tableaux de bord simples.',
    'On identifie où l\u2019argent fuit chaque mois.',
    'On récupère 3 à 5 points de marge nette.',
  ] },
  { n: '06', title: 'Fiche de poste & performance', price: 1690, bullets: [
    'On rédige les missions de chaque poste.',
    'On définit des objectifs chiffrés par personne.',
    'On construit un système de primes rentable.',
    'On met en place le suivi mensuel de performance.',
    'On augmente votre productivité de 20 %.',
  ] },
  { n: '07', title: 'Livraison & plateformes', price: 1490, bullets: [
    'On audite vos comptes Uber Eats, Deliveroo, Glovo.',
    'On adapte votre carte au digital pour maximiser la marge.',
    'On règle les visuels et fiches plats pour convertir.',
    'On négocie vos commissions et zones de livraison.',
    'On vise 30 à 40 % de CA additionnel via la livraison.',
  ] },
  { n: '08', title: 'Création du packaging', price:  890, bullets: [
    'On dessine vos emballages take-away signés.',
    'On sélectionne les fournisseurs éco-responsables.',
    'On imprime votre marque sur tous les supports.',
    'On négocie les prix par volume.',
    'On rend votre marque cohérente jusque chez le client.',
  ] },
  { n: '09', title: 'Merch & extension', price: 2990, bullets: [
    'On dessine votre ligne de t-shirts, tote bags, mugs.',
    'On sélectionne les fournisseurs de qualité.',
    'On calcule les marges à la revente.',
    'On met en place les stocks et la vente en boutique.',
    'On transforme votre marque en revenu additionnel.',
  ] },
  { n: '10', title: 'Univers visuel & Calendrier éditorial', price: 2990, bullets: [
    'On écrit votre ligne éditoriale sur Insta et TikTok.',
    'On crée vos templates de posts et stories.',
    'On cadence votre calendrier de publications sur 2 mois.',
    'On forme votre équipe à filmer facilement.',
    'On rend votre marque désirable sur tous les canaux.',
  ] },
  { n: '11', title: 'Opening · Présence sur place', price: 2490, bullets: [
    'On est sur place le jour du lancement.',
    'On règle les derniers détails opérationnels en direct.',
    'On coache votre équipe pendant les premiers services.',
    'On orchestre la communication du lancement.',
    'On sécurise vos 30 premiers jours d\u2019exploitation.',
  ] },
];
const priceOfN = (n) => CHANTIERS.find((c) => c.n === n)?.price || 0;

// ─── Places qui se libèrent (info affichée à l'étape finale) ────────
const WAITLIST_SLOTS = [
  { date: '10 août 2026',     count: 2 },
  { date: '8 septembre 2026', count: 1 },
  { date: '10 octobre 2026',  count: 2 },
];

// ═════════════════════════════════════════════════════════════════════
// Progress dots — indicateur d'étape éditorial
// ═════════════════════════════════════════════════════════════════════
function StepDots({ step, total }) {
  return (
    <div className="flex items-center justify-center gap-2" data-testid="wizard-progress">
      {Array.from({ length: total }).map((_, i) => {
        const active = i === step;
        const done = i < step;
        return (
          <span
            key={i}
            style={{
              width: active ? 28 : 8, height: 4, borderRadius: 999,
              background: done ? DA.goldDeep : (active ? DA.goldMain : 'rgba(60,60,67,0.15)'),
              transition: 'all 0.32s cubic-bezier(0.32,0.72,0,1)',
            }}
          />
        );
      })}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// SectionHeader — overline + titre éditorial
// ═════════════════════════════════════════════════════════════════════
function WizardHeader({ overline, title, subtitle }) {
  return (
    <div className="text-center mb-8">
      <p style={{
        fontFamily: DA.mono, fontSize: 10, fontWeight: 800,
        letterSpacing: '0.34em', textTransform: 'uppercase',
        color: DA.goldDeep, marginBottom: 10,
      }}>
        {overline}
      </p>
      <h2 style={{
        fontFamily: DA.serif, fontSize: 'clamp(30px, 5.5vw, 42px)', fontWeight: 400,
        letterSpacing: '-0.03em', color: DA.greenDeep, lineHeight: 1.02,
        fontStyle: 'italic',
      }}>
        {title}
      </h2>
      {subtitle && (
        <p style={{
          marginTop: 12, fontFamily: DA.sans, fontSize: 14, color: DA.inkBody,
          lineHeight: 1.55, letterSpacing: '-0.005em', maxWidth: 520, margin: '12px auto 0',
        }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// STEP 1 · FORMAT
// ═════════════════════════════════════════════════════════════════════
function StepFormat({ selected, onSelect }) {
  return (
    <div data-testid="wizard-step-format">
      <WizardHeader
        overline="Étape 1 sur 4"
        title="Votre format."
        subtitle="Choisissez la façon dont vous souhaitez que je travaille avec vous."
      />
      <div className="grid gap-4 md:grid-cols-3 max-w-5xl mx-auto">
        {FORMATS.map((f) => {
          const Icon = f.icon;
          const isActive = selected === f.key;
          return (
            <button
              type="button"
              key={f.key}
              onClick={() => onSelect(f.key)}
              className="text-left transition-all active:scale-[0.995]"
              style={{
                background: isActive ? DA.greenDeep : DA.paper,
                color: isActive ? DA.ivory : DA.inkBody,
                border: `0.5px solid ${isActive ? DA.goldMain : DA.goldSoft}`,
                borderRadius: 18,
                padding: '28px 24px',
                boxShadow: isActive
                  ? '0 30px 60px -20px rgba(11,43,36,0.55), inset 0 0 0 3px rgba(216,148,64,0.14)'
                  : '0 12px 30px -14px rgba(11,43,36,0.15)',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
              }}
              data-testid={`format-card-${f.key}`}
              aria-pressed={isActive}
            >
              {/* Highlight ribbon */}
              {f.highlight && (
                <span style={{
                  position: 'absolute', top: 14, right: 14,
                  fontFamily: DA.mono, fontSize: 8.5, fontWeight: 800,
                  letterSpacing: '0.28em', textTransform: 'uppercase',
                  color: isActive ? DA.goldMain : DA.goldDeep,
                  padding: '4px 10px', borderRadius: 999,
                  background: isActive ? 'rgba(216,148,64,0.12)' : 'rgba(216,148,64,0.10)',
                  border: `0.5px solid ${DA.goldSoft}`,
                }}>
                  {f.highlight}
                </span>
              )}

              {/* Icon */}
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 44, height: 44, borderRadius: 12,
                background: isActive ? 'rgba(216,148,64,0.16)' : DA.ivoryDeep,
                border: `0.5px solid ${isActive ? DA.goldMain : DA.goldSoft}`,
                marginBottom: 18,
              }}>
                <Icon size={20} strokeWidth={1.8} style={{ color: isActive ? DA.goldMain : DA.goldDeep }} />
              </span>

              {/* Label */}
              <p style={{
                fontFamily: DA.serif, fontSize: 22, fontWeight: 500, fontStyle: 'italic',
                letterSpacing: '-0.02em', lineHeight: 1.1,
                color: isActive ? DA.ivory : DA.greenDeep, marginBottom: 4,
              }}>
                {f.label}
              </p>
              <p style={{
                fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
                letterSpacing: '0.26em', textTransform: 'uppercase',
                color: isActive ? DA.goldMain : DA.inkMuted, marginBottom: 14,
              }}>
                {f.duration}
              </p>

              {/* Price */}
              <div style={{ padding: '12px 0', borderTop: `0.5px solid ${isActive ? 'rgba(216,148,64,0.28)' : DA.goldSoft}`, borderBottom: `0.5px solid ${isActive ? 'rgba(216,148,64,0.28)' : DA.goldSoft}`, marginBottom: 16 }}>
                <p style={{
                  fontFamily: DA.sans, fontSize: 17, fontWeight: 800,
                  color: isActive ? DA.goldMain : DA.greenDeep, letterSpacing: '-0.01em',
                }}>
                  {f.priceLabel}
                </p>
                <p style={{
                  fontFamily: DA.sans, fontSize: 11.5, fontWeight: 500, fontStyle: 'italic',
                  color: isActive ? 'rgba(255,255,255,0.72)' : DA.inkMuted, marginTop: 2,
                }}>
                  {f.priceSub}
                </p>
              </div>

              {/* Tagline */}
              <p style={{
                fontFamily: DA.serif, fontSize: 13.5, fontStyle: 'italic',
                color: isActive ? 'rgba(255,255,255,0.88)' : DA.inkBody,
                lineHeight: 1.5, marginBottom: 14,
              }}>
                {f.tagline}
              </p>

              {/* Included list */}
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {f.included.map((it, idx) => (
                  <li key={idx} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                    fontFamily: DA.sans, fontSize: 12.5,
                    color: isActive ? 'rgba(255,255,255,0.82)' : DA.inkBody,
                    lineHeight: 1.45, marginBottom: 6,
                  }}>
                    <Check size={12} strokeWidth={2.6} style={{ color: isActive ? DA.goldMain : DA.goldDeep, marginTop: 4, flexShrink: 0 }} />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>

              {/* Active indicator */}
              {isActive && (
                <div style={{
                  marginTop: 18, padding: '10px 12px', borderRadius: 10,
                  background: 'rgba(216,148,64,0.14)',
                  border: `0.5px solid ${DA.goldMain}`,
                  fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
                  letterSpacing: '0.28em', textTransform: 'uppercase',
                  color: DA.goldMain, textAlign: 'center',
                }}>
                  ✓ Sélectionné
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// STEP 2 · PAYS + VILLE
// ═════════════════════════════════════════════════════════════════════
function StepLocation({ country, city, onCountry, onCity }) {
  return (
    <div data-testid="wizard-step-location">
      <WizardHeader
        overline="Étape 2 sur 4"
        title="Où êtes-vous ?"
        subtitle="Pour organiser les déplacements si votre format inclut du terrain."
      />
      <div className="max-w-xl mx-auto flex flex-col gap-5">
        {/* Country dropdown */}
        <div>
          <label style={{
            display: 'block',
            fontFamily: DA.mono, fontSize: 9.5, fontWeight: 800,
            letterSpacing: '0.28em', textTransform: 'uppercase',
            color: DA.goldDeep, marginBottom: 10,
          }}>
            Pays
          </label>
          <div style={{ position: 'relative' }}>
            <MapPin size={16} strokeWidth={1.8} style={{
              position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
              color: DA.goldDeep, pointerEvents: 'none',
            }} />
            <select
              value={country || ''}
              onChange={(e) => onCountry(e.target.value)}
              className="w-full appearance-none"
              style={{
                background: DA.paper,
                border: `0.5px solid ${DA.goldSoft}`,
                borderRadius: 14,
                padding: '18px 44px 18px 44px',
                fontFamily: DA.serif, fontSize: 16, fontWeight: 500,
                color: country ? DA.greenDeep : DA.inkMuted,
                letterSpacing: '-0.01em',
                cursor: 'pointer',
                outline: 'none',
              }}
              data-testid="wizard-country-select"
            >
              <option value="" disabled>Sélectionnez votre pays</option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.flag} {c.label}</option>
              ))}
            </select>
            <ChevronRight size={16} strokeWidth={2} style={{
              position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%) rotate(90deg)',
              color: DA.inkMuted, pointerEvents: 'none',
            }} />
          </div>
        </div>

        {/* City free text */}
        <div>
          <label style={{
            display: 'block',
            fontFamily: DA.mono, fontSize: 9.5, fontWeight: 800,
            letterSpacing: '0.28em', textTransform: 'uppercase',
            color: DA.goldDeep, marginBottom: 10,
          }}>
            Ville
          </label>
          <input
            type="text"
            value={city}
            onChange={(e) => onCity(e.target.value)}
            placeholder="Paris, Barcelone, Marrakech…"
            style={{
              width: '100%',
              background: DA.paper,
              border: `0.5px solid ${DA.goldSoft}`,
              borderRadius: 14,
              padding: '18px 20px',
              fontFamily: DA.serif, fontSize: 16, fontWeight: 500,
              color: DA.greenDeep, letterSpacing: '-0.01em',
              outline: 'none',
            }}
            data-testid="wizard-city-input"
          />
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// STEP 3 · CHANTIERS (avec récap format + prix/inclus badges + total)
// ═════════════════════════════════════════════════════════════════════
function StepChantiers({ selectedChantiers, onToggle, onSelectAll, formatKey }) {
  const format = FORMATS.find((f) => f.key === formatKey);
  const isHybride = formatKey === 'hybride';
  const basePrice = FORMAT_BASE_PRICE[formatKey] || 0;
  const chantiersCount = selectedChantiers.length;
  const chantiersCost = isHybride ? 0 : selectedChantiers.reduce((s, n) => s + priceOfN(n), 0);
  const totalHT = isHybride ? FORMAT_BASE_PRICE.hybride : basePrice + chantiersCost;
  const allSelected = selectedChantiers.length === CHANTIERS.length;
  const hasNonFoundationSelected = !isHybride && selectedChantiers.some((n) => !FOUNDATION_KEYS.includes(n));

  return (
    <div data-testid="wizard-step-chantiers">
      <WizardHeader
        overline="Étape 3 sur 4"
        title={isHybride ? 'Votre programme complet.' : 'Vos chantiers optionnels.'}
        subtitle={isHybride
          ? 'Votre format inclut déjà l\u2019intégralité des 12 chantiers. Voici ce que nous allons construire ensemble.'
          : 'Cochez uniquement les leviers que vous souhaitez ajouter. Vous n\u2019avez rien à cocher pour valider votre base.'}
      />

      {/* ═══ Récap format sélectionné (sticky au-dessus des chantiers) ═══ */}
      <div className="max-w-3xl mx-auto mb-6">
        <div
          style={{
            background: DA.greenDeep,
            color: DA.ivory,
            border: `0.5px solid ${DA.goldMain}`,
            borderRadius: 16,
            padding: '18px 20px',
            boxShadow: '0 20px 44px -18px rgba(11,43,36,0.55), inset 0 0 0 3px rgba(216,148,64,0.10)',
          }}
          data-testid="wizard-format-recap-card"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <p style={{
                fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
                letterSpacing: '0.28em', textTransform: 'uppercase',
                color: DA.goldMain, marginBottom: 4,
              }}>
                Votre format
              </p>
              <p style={{
                fontFamily: DA.serif, fontSize: 22, fontWeight: 500, fontStyle: 'italic',
                letterSpacing: '-0.02em', color: DA.ivory, lineHeight: 1.1,
              }}>
                {format?.label}
              </p>
              <p style={{
                fontFamily: DA.sans, fontSize: 11, color: 'rgba(255,255,255,0.68)',
                marginTop: 4, letterSpacing: '-0.005em',
              }}>
                {format?.duration}
              </p>
            </div>
            <div className="text-right">
              <p style={{
                fontFamily: DA.mono, fontSize: 8.5, fontWeight: 800,
                letterSpacing: '0.24em', textTransform: 'uppercase',
                color: 'rgba(216,148,64,0.75)', marginBottom: 2,
              }}>
                Base
              </p>
              <p style={{
                fontFamily: DA.sans, fontSize: 18, fontWeight: 800,
                color: DA.goldMain, letterSpacing: '-0.01em',
              }}>
                {fmt(basePrice)}&nbsp;€ HT
              </p>
            </div>
          </div>

          <div style={{ borderTop: '0.5px solid rgba(216,148,64,0.22)', paddingTop: 12 }}>
            <p style={{
              fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
              letterSpacing: '0.28em', textTransform: 'uppercase',
              color: DA.goldMain, marginBottom: 8,
            }}>
              {isHybride ? 'Ce qui est inclus (12 chantiers + suivi terrain)' : 'Ce qui est déjà inclus dans la base'}
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {format?.included.map((item, i) => (
                <li key={i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  fontFamily: DA.serif, fontSize: 13.5, fontStyle: 'italic',
                  color: 'rgba(255,255,255,0.92)', lineHeight: 1.45, marginBottom: 5,
                }}>
                  <Check size={13} strokeWidth={2.6} style={{ color: DA.goldMain, marginTop: 4, flexShrink: 0 }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* ═══ Titre section chantiers ═══ */}
      <div className="max-w-3xl mx-auto mb-4 px-2">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <p style={{
              fontFamily: DA.mono, fontSize: 9.5, fontWeight: 800,
              letterSpacing: '0.28em', textTransform: 'uppercase',
              color: DA.goldDeep, marginBottom: 4,
            }}>
              {isHybride ? 'Les 12 chantiers inclus' : 'Chantiers en option — À la carte'}
            </p>
            <p style={{
              fontFamily: DA.serif, fontSize: 20, fontWeight: 500, fontStyle: 'italic',
              color: DA.greenDeep, letterSpacing: '-0.02em', lineHeight: 1.1,
            }}>
              {isHybride
                ? 'Tout est prévu.'
                : `${chantiersCount} / ${CHANTIERS.length} chantiers ajoutés`}
            </p>
            {!isHybride && (
              <p style={{
                fontFamily: DA.sans, fontSize: 11.5, color: DA.inkMuted,
                marginTop: 3, fontStyle: 'italic',
              }}>
                À partir de {fmt(690)}&nbsp;€ HT — prix affiché sur chaque chantier
              </p>
            )}
          </div>
          {!isHybride && (
            <button
              type="button"
              onClick={() => onSelectAll(!allSelected)}
              style={{
                fontFamily: DA.mono, fontSize: 10, fontWeight: 800,
                letterSpacing: '0.22em', textTransform: 'uppercase',
                color: DA.goldDeep, background: 'transparent', border: `0.5px solid ${DA.goldSoft}`,
                borderRadius: 999, cursor: 'pointer', padding: '8px 14px',
                whiteSpace: 'nowrap',
              }}
              data-testid="wizard-chantiers-select-all"
            >
              {allSelected ? 'Tout décocher' : 'Tout cocher'}
            </button>
          )}
        </div>
      </div>

      {/* ═══ Règle des fondations (chantiers 01 & 02) ═══ */}
      {!isHybride && (
        <div className="max-w-3xl mx-auto mb-4" data-testid="wizard-foundation-note">
          <div
            style={{
              background: DA.paper,
              border: `0.5px solid ${DA.goldSoft}`,
              borderRadius: 12,
              padding: '14px 16px',
              display: 'flex', alignItems: 'flex-start', gap: 12,
            }}
          >
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, width: 32, height: 32, borderRadius: 999,
              background: 'rgba(216,148,64,0.14)', border: `0.5px solid ${DA.goldMain}`,
              fontFamily: DA.serif, fontSize: 14, fontWeight: 800, color: DA.goldDeep,
            }}>
              ⚑
            </span>
            <div className="flex-1 min-w-0">
              <p style={{
                fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
                letterSpacing: '0.24em', textTransform: 'uppercase',
                color: DA.goldDeep, marginBottom: 4,
              }}>
                La règle des fondations
              </p>
              <p style={{
                fontFamily: DA.serif, fontSize: 13.5, color: DA.inkBody,
                lineHeight: 1.5, letterSpacing: '-0.005em',
              }}>
                Les chantiers <strong style={{ color: DA.greenDeep }}>00 · Coaching dirigeant</strong> et
                <strong style={{ color: DA.greenDeep }}> 01 · ADN & histoire de marque</strong> sont les fondations de votre projet.
                Dès qu&apos;on ajoute un autre chantier, ils sont ajoutés automatiquement — c&apos;est ce qui garantit
                la cohérence et les résultats de tout le reste.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Liste des 12 chantiers ═══ */}
      <div className="max-w-3xl mx-auto">
        <div className="flex flex-col gap-2">
          {CHANTIERS.map((c) => {
            const isSelected = selectedChantiers.includes(c.n);
            const isFoundationAutoLocked = c.foundation && hasNonFoundationSelected;
            const isLocked = isHybride || isFoundationAutoLocked; // verrouillé si Hybride OU fondation auto-ajoutée
            return (
              <button
                type="button"
                key={c.n}
                onClick={() => !isLocked && onToggle(c.n)}
                disabled={isLocked}
                className="text-left transition-all active:scale-[0.998]"
                style={{
                  background: isLocked
                    ? 'rgba(216,148,64,0.06)'
                    : (isSelected ? 'rgba(11,43,36,0.04)' : DA.paper),
                  border: `0.5px solid ${(isLocked || isSelected) ? DA.goldMain : DA.goldSoft}`,
                  borderRadius: 14,
                  padding: '16px 18px',
                  cursor: isLocked ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'flex-start', gap: 14,
                  opacity: 1,
                }}
                data-testid={`wizard-chantier-${c.n}`}
                aria-pressed={isSelected}
                aria-disabled={isLocked}
              >
                {/* Checkbox / lock visual */}
                <span
                  style={{
                    flexShrink: 0, marginTop: 2,
                    width: 22, height: 22, borderRadius: 6,
                    background: (isLocked || isSelected) ? DA.greenDeep : 'transparent',
                    border: `1.5px solid ${(isLocked || isSelected) ? DA.greenDeep : DA.goldSoft}`,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.18s ease',
                  }}
                >
                  {(isLocked || isSelected) && <Check size={14} strokeWidth={3} style={{ color: DA.goldMain }} />}
                </span>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 mb-2 flex-wrap">
                    <span style={{
                      fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
                      letterSpacing: '0.24em', color: DA.goldDeep,
                    }}>
                      {c.n}
                    </span>
                    <span style={{
                      fontFamily: DA.serif, fontSize: 15.5, fontWeight: 700,
                      color: DA.greenDeep, letterSpacing: '-0.015em', lineHeight: 1.2,
                    }}>
                      {c.title}
                    </span>
                    {c.foundation && (
                      <span style={{
                        fontFamily: DA.mono, fontSize: 8.5, fontWeight: 800,
                        letterSpacing: '0.22em', textTransform: 'uppercase',
                        color: DA.goldDeep, background: 'rgba(216,148,64,0.14)',
                        border: `0.5px solid ${DA.goldSoft}`, borderRadius: 999,
                        padding: '2px 8px',
                      }}>
                        Fondation
                      </span>
                    )}
                  </div>

                  {/* Bullets "On…" — action, pas mots de consultant */}
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 10px' }}>
                    {(c.bullets || []).map((b, i) => (
                      <li key={i} style={{
                        display: 'flex', alignItems: 'flex-start', gap: 7,
                        fontFamily: DA.sans, fontSize: 12.5, color: DA.inkBody,
                        lineHeight: 1.45, marginBottom: 4, letterSpacing: '-0.005em',
                      }}>
                        <Check size={11} strokeWidth={2.8} style={{
                          color: DA.goldDeep, marginTop: 4, flexShrink: 0,
                        }} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Price / Inclus / Fondation badge */}
                  {isLocked ? (
                    <span
                      style={{
                        display: 'inline-block',
                        fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
                        letterSpacing: '0.24em', textTransform: 'uppercase',
                        color: DA.greenDeep,
                        background: 'rgba(216,148,64,0.20)',
                        border: `0.5px solid ${DA.goldMain}`,
                        borderRadius: 999, padding: '4px 10px',
                      }}
                      data-testid={`chantier-${c.n}-inclus-badge`}
                    >
                      {isHybride
                        ? '✓ Inclus dans votre pack'
                        : `✓ Fondation · ${fmt(c.price)} € HT`}
                    </span>
                  ) : (
                    <span
                      style={{
                        display: 'inline-block',
                        fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
                        letterSpacing: '0.22em', textTransform: 'uppercase',
                        color: isSelected ? DA.greenDeep : DA.goldDeep,
                        background: isSelected ? 'rgba(216,148,64,0.20)' : 'transparent',
                        border: `0.5px solid ${DA.goldSoft}`,
                        borderRadius: 999, padding: '4px 10px',
                      }}
                      data-testid={`chantier-${c.n}-price-badge`}
                    >
                      À partir de {fmt(c.price)}&nbsp;€ HT
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══ Total dynamique en bas ═══ */}
      <div className="max-w-3xl mx-auto mt-6" data-testid="wizard-total-panel">
        <div
          style={{
            background: DA.paper,
            border: `0.5px solid ${DA.goldMain}`,
            borderRadius: 14,
            padding: '18px 20px',
            boxShadow: '0 14px 30px -14px rgba(216,148,64,0.32)',
          }}
        >
          <p style={{
            fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
            letterSpacing: '0.28em', textTransform: 'uppercase',
            color: DA.goldDeep, marginBottom: 8,
          }}>
            Votre configuration
          </p>

          {isHybride ? (
            <div className="flex items-baseline justify-between gap-4">
              <span style={{
                fontFamily: DA.serif, fontSize: 15, fontStyle: 'italic',
                color: DA.inkBody, letterSpacing: '-0.005em',
              }}>
                Tout inclus · les 12 chantiers
              </span>
              <span style={{
                fontFamily: DA.sans, fontSize: 22, fontWeight: 800,
                color: DA.greenDeep, letterSpacing: '-0.02em',
              }}>
                {fmt(totalHT)}&nbsp;€ HT
              </span>
            </div>
          ) : (
            <>
              <div className="flex items-baseline justify-between mb-1.5">
                <span style={{ fontFamily: DA.serif, fontSize: 13.5, fontStyle: 'italic', color: DA.inkBody }}>
                  Base · {format?.label}
                </span>
                <span style={{ fontFamily: DA.sans, fontSize: 14, fontWeight: 700, color: DA.greenDeep }}>
                  {fmt(basePrice)}&nbsp;€
                </span>
              </div>
              <div className="flex items-baseline justify-between mb-1.5">
                <span style={{ fontFamily: DA.serif, fontSize: 13.5, fontStyle: 'italic', color: DA.inkBody }}>
                  {chantiersCount} chantier{chantiersCount > 1 ? 's' : ''} en option
                </span>
                <span style={{ fontFamily: DA.sans, fontSize: 14, fontWeight: 700, color: DA.greenDeep }}>
                  {fmt(chantiersCost)}&nbsp;€
                </span>
              </div>
              <div className="flex items-baseline justify-between pt-2 mt-2" style={{ borderTop: `0.5px solid ${DA.goldSoft}` }}>
                <span style={{
                  fontFamily: DA.mono, fontSize: 10, fontWeight: 800,
                  letterSpacing: '0.24em', textTransform: 'uppercase',
                  color: DA.goldDeep,
                }}>
                  Total HT
                </span>
                <span style={{
                  fontFamily: DA.sans, fontSize: 22, fontWeight: 800,
                  color: DA.greenDeep, letterSpacing: '-0.02em',
                }} data-testid="wizard-total-amount">
                  {fmt(totalHT)}&nbsp;€
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// STEP 4 · CONTACT + DEVIS COMPLET + LISTE D'ATTENTE
// ═════════════════════════════════════════════════════════════════════
function StepContact({ contact, onChange, formatKey, country, city, selectedChantiers }) {
  const format = FORMATS.find((f) => f.key === formatKey);
  const isHybride = formatKey === 'hybride';
  const countryLabel = COUNTRIES.find((c) => c.code === country)?.label || country || '—';
  const basePrice = FORMAT_BASE_PRICE[formatKey] || 0;
  const chantiersItems = (isHybride ? CHANTIERS.map((c) => c.n) : selectedChantiers)
    .map((n) => CHANTIERS.find((c) => c.n === n))
    .filter(Boolean);
  const chantiersCost = isHybride ? 0 : chantiersItems.reduce((s, c) => s + c.price, 0);
  const totalHT = isHybride ? FORMAT_BASE_PRICE.hybride : basePrice + chantiersCost;

  return (
    <div data-testid="wizard-step-contact">
      <WizardHeader
        overline="Étape 4 sur 4 · Dernière étape"
        title="Votre devis & vos coordonnées."
        subtitle="Vérifiez votre configuration ci-dessous, puis renseignez vos coordonnées. Je vous recontacterai personnellement sous 72h."
      />

      <div className="max-w-2xl mx-auto">

        {/* ══════════════ DEVIS DÉTAILLÉ (style facture Hermès) ══════════════ */}
        <div
          className="mb-6"
          style={{
            background: DA.paper,
            border: `0.5px solid ${DA.goldMain}`,
            borderRadius: 16,
            padding: '22px 22px 20px',
            boxShadow: '0 20px 44px -18px rgba(11,43,36,0.18)',
          }}
          data-testid="wizard-devis"
        >
          {/* Devis header */}
          <div className="flex items-center justify-between mb-4 pb-3" style={{ borderBottom: `0.5px solid ${DA.goldSoft}` }}>
            <div>
              <p style={{
                fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
                letterSpacing: '0.32em', textTransform: 'uppercase',
                color: DA.goldDeep,
              }}>
                Votre devis
              </p>
              <p style={{
                fontFamily: DA.serif, fontSize: 13, fontStyle: 'italic',
                color: DA.inkMuted, marginTop: 3,
              }}>
                Simulation · sans engagement
              </p>
            </div>
            <div className="text-right">
              <p style={{
                fontFamily: DA.mono, fontSize: 8.5, fontWeight: 700,
                letterSpacing: '0.24em', textTransform: 'uppercase',
                color: DA.inkMuted,
              }}>
                {city ? `${city}, ${countryLabel}` : countryLabel}
              </p>
              <p style={{
                fontFamily: DA.mono, fontSize: 8.5, fontWeight: 700,
                letterSpacing: '0.24em', textTransform: 'uppercase',
                color: DA.inkMuted, marginTop: 2,
              }}>
                Réf. SP-{new Date().getFullYear()}-{Math.floor(Math.random() * 900 + 100)}
              </p>
            </div>
          </div>

          {/* ── Section 1 · Format & ce qui est inclus ── */}
          <div className="mb-5">
            <div className="flex items-baseline justify-between mb-3 gap-3">
              <div>
                <p style={{
                  fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
                  letterSpacing: '0.24em', textTransform: 'uppercase',
                  color: DA.goldDeep, marginBottom: 4,
                }}>
                  Formule sélectionnée
                </p>
                <p style={{
                  fontFamily: DA.serif, fontSize: 20, fontWeight: 500, fontStyle: 'italic',
                  color: DA.greenDeep, letterSpacing: '-0.02em', lineHeight: 1.1,
                }}>
                  {format?.label}
                </p>
                <p style={{
                  fontFamily: DA.sans, fontSize: 11.5, color: DA.inkMuted,
                  marginTop: 3, fontStyle: 'italic',
                }}>
                  {format?.duration}
                </p>
              </div>
              <p style={{
                fontFamily: DA.sans, fontSize: 17, fontWeight: 800,
                color: DA.greenDeep, letterSpacing: '-0.01em', whiteSpace: 'nowrap',
              }}>
                {fmt(basePrice)}&nbsp;€ HT
              </p>
            </div>

            <div style={{
              background: 'rgba(11,43,36,0.04)',
              border: `0.5px solid ${DA.goldSoft}`,
              borderRadius: 10, padding: '12px 14px',
            }}>
              <p style={{
                fontFamily: DA.mono, fontSize: 8.5, fontWeight: 800,
                letterSpacing: '0.24em', textTransform: 'uppercase',
                color: DA.goldDeep, marginBottom: 8,
              }}>
                {isHybride ? 'Tout ce qui est compris' : 'Compris dans la base'}
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {format?.included.map((it, i) => (
                  <li key={i} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 7,
                    fontFamily: DA.serif, fontSize: 13, fontStyle: 'italic',
                    color: DA.inkBody, lineHeight: 1.4, marginBottom: 4,
                  }}>
                    <Check size={12} strokeWidth={2.6} style={{ color: DA.goldDeep, marginTop: 3, flexShrink: 0 }} />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* ── Section 2 · Chantiers sélectionnés (liste détaillée) ── */}
          <div className="mb-5">
            <p style={{
              fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
              letterSpacing: '0.24em', textTransform: 'uppercase',
              color: DA.goldDeep, marginBottom: 10,
            }}>
              {isHybride
                ? `Les 12 chantiers inclus (${chantiersItems.length})`
                : `Chantiers ajoutés (${chantiersItems.length})`}
            </p>

            {chantiersItems.length === 0 ? (
              <div style={{
                background: 'rgba(216,148,64,0.06)',
                border: `0.5px dashed ${DA.goldSoft}`,
                borderRadius: 10, padding: '14px 16px',
              }} data-testid="wizard-devis-no-chantiers">
                <p style={{
                  fontFamily: DA.serif, fontSize: 13, fontStyle: 'italic',
                  color: DA.inkBody, lineHeight: 1.5,
                }}>
                  Aucun chantier ajouté pour l&apos;instant. Nous pourrons en discuter ensemble et ajuster votre programme sur mesure lors de notre premier échange.
                </p>
              </div>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} data-testid="wizard-devis-chantiers-list">
                {chantiersItems.map((c, idx) => (
                  <li
                    key={c.n}
                    className="flex items-baseline justify-between gap-3"
                    style={{
                      padding: '9px 0',
                      borderBottom: idx < chantiersItems.length - 1 ? `0.5px solid ${DA.goldSoft}` : 'none',
                    }}
                    data-testid={`devis-item-${c.n}`}
                  >
                    <div className="flex-1 min-w-0 flex items-baseline gap-2">
                      <span style={{
                        fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
                        letterSpacing: '0.22em', color: DA.goldDeep, flexShrink: 0,
                      }}>
                        {c.n}
                      </span>
                      <span style={{
                        fontFamily: DA.serif, fontSize: 13.5, fontWeight: 500,
                        color: DA.greenDeep, letterSpacing: '-0.005em', lineHeight: 1.3,
                      }}>
                        {c.title}
                      </span>
                    </div>
                    {isHybride ? (
                      <span style={{
                        fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
                        letterSpacing: '0.20em', color: DA.goldDeep, whiteSpace: 'nowrap',
                      }}>
                        Inclus
                      </span>
                    ) : (
                      <span style={{
                        fontFamily: DA.sans, fontSize: 13, fontWeight: 700,
                        color: DA.greenDeep, whiteSpace: 'nowrap', letterSpacing: '-0.005em',
                      }}>
                        {fmt(c.price)}&nbsp;€
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── Section 3 · Récap total ── */}
          <div style={{ borderTop: `0.5px solid ${DA.goldMain}`, paddingTop: 14 }}>
            {!isHybride && (
              <>
                <div className="flex items-baseline justify-between mb-1.5">
                  <span style={{ fontFamily: DA.serif, fontSize: 13, fontStyle: 'italic', color: DA.inkBody }}>
                    Sous-total formule
                  </span>
                  <span style={{ fontFamily: DA.sans, fontSize: 13.5, fontWeight: 700, color: DA.greenDeep }}>
                    {fmt(basePrice)}&nbsp;€
                  </span>
                </div>
                <div className="flex items-baseline justify-between mb-1.5">
                  <span style={{ fontFamily: DA.serif, fontSize: 13, fontStyle: 'italic', color: DA.inkBody }}>
                    Sous-total chantiers ({chantiersItems.length})
                  </span>
                  <span style={{ fontFamily: DA.sans, fontSize: 13.5, fontWeight: 700, color: DA.greenDeep }}>
                    {fmt(chantiersCost)}&nbsp;€
                  </span>
                </div>
              </>
            )}
            <div className="flex items-baseline justify-between pt-3 mt-2" style={{ borderTop: `0.5px solid ${DA.goldSoft}` }}>
              <div>
                <p style={{
                  fontFamily: DA.mono, fontSize: 10, fontWeight: 800,
                  letterSpacing: '0.24em', textTransform: 'uppercase', color: DA.goldDeep,
                }}>
                  Total HT
                </p>
                <p style={{
                  fontFamily: DA.sans, fontSize: 10.5, fontStyle: 'italic', color: DA.inkMuted, marginTop: 2,
                }}>
                  TVA non applicable · art. 293 B du CGI
                </p>
              </div>
              <p
                data-testid="wizard-devis-total"
                style={{
                  fontFamily: DA.sans, fontSize: 26, fontWeight: 800,
                  color: DA.greenDeep, letterSpacing: '-0.02em',
                }}
              >
                {fmt(totalHT)}&nbsp;€
              </p>
            </div>
          </div>
        </div>

        {/* Places qui se libèrent — info exclusive */}
        <div
          className="mb-6"
          style={{
            background: `linear-gradient(135deg, ${DA.greenDeep} 0%, #0F3A31 100%)`,
            color: DA.ivory,
            border: `0.5px solid ${DA.goldMain}`,
            borderRadius: 14,
            padding: '18px 20px',
            boxShadow: '0 20px 40px -18px rgba(11,43,36,0.55), inset 0 0 0 3px rgba(216,148,64,0.10)',
          }}
          data-testid="wizard-waitlist-info"
        >
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={14} strokeWidth={2} style={{ color: DA.goldMain }} />
            <p style={{
              fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
              letterSpacing: '0.28em', textTransform: 'uppercase',
              color: DA.goldMain,
            }}>
              Prochaines places disponibles
            </p>
          </div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {WAITLIST_SLOTS.map((slot, i) => (
              <li key={i} className="flex items-center justify-between" style={{
                padding: '10px 0',
                borderBottom: i < WAITLIST_SLOTS.length - 1 ? `0.5px solid rgba(216,148,64,0.22)` : 'none',
              }}>
                <span style={{ fontFamily: DA.serif, fontSize: 14, fontWeight: 500, fontStyle: 'italic', color: DA.ivory }}>
                  À partir du {slot.date}
                </span>
                <span style={{
                  fontFamily: DA.mono, fontSize: 10.5, fontWeight: 800,
                  letterSpacing: '0.14em', color: DA.goldMain,
                  background: 'rgba(216,148,64,0.14)', padding: '4px 10px', borderRadius: 999,
                  border: `0.5px solid rgba(216,148,64,0.32)`,
                }}>
                  {slot.count} place{slot.count > 1 ? 's' : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Form */}
        <p style={{
          fontFamily: DA.mono, fontSize: 10, fontWeight: 800,
          letterSpacing: '0.28em', textTransform: 'uppercase',
          color: DA.goldDeep, marginBottom: 12, marginTop: 4,
        }}>
          Vos coordonnées
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <WizardField
            id="firstName" label="Prénom" icon={User}
            value={contact.firstName}
            onChange={(v) => onChange({ firstName: v })}
            placeholder="Sabril"
            testid="wizard-firstname"
          />
          <WizardField
            id="restaurant" label="Nom de votre établissement" icon={Store}
            value={contact.restaurant}
            onChange={(v) => onChange({ restaurant: v })}
            placeholder="Maison Avocado"
            testid="wizard-restaurant"
          />
          <WizardField
            id="email" label="Email" type="email" icon={Mail}
            value={contact.email}
            onChange={(v) => onChange({ email: v })}
            placeholder="vous@restaurant.com"
            testid="wizard-email"
          />
          <WizardField
            id="phone" label="Téléphone (WhatsApp)" type="tel" icon={Phone}
            value={contact.phone}
            onChange={(v) => onChange({ phone: v })}
            placeholder="+33 6 12 34 56 78"
            testid="wizard-phone"
          />
        </div>

        <div className="mt-3">
          <label style={{
            display: 'block',
            fontFamily: DA.mono, fontSize: 9.5, fontWeight: 800,
            letterSpacing: '0.28em', textTransform: 'uppercase',
            color: DA.goldDeep, marginBottom: 10,
          }}>
            <MessageSquare size={12} style={{ display: 'inline', verticalAlign: -1, marginRight: 6, color: DA.goldDeep }} />
            Votre projet en quelques lignes
          </label>
          <textarea
            value={contact.notes}
            onChange={(e) => onChange({ notes: e.target.value })}
            placeholder="Décrivez votre situation actuelle, votre objectif, ce qui vous a amené jusqu'ici…"
            rows={4}
            style={{
              width: '100%',
              background: DA.paper,
              border: `0.5px solid ${DA.goldSoft}`,
              borderRadius: 14,
              padding: '14px 18px',
              fontFamily: DA.serif, fontSize: 14, fontWeight: 500,
              color: DA.greenDeep, letterSpacing: '-0.005em',
              lineHeight: 1.55, resize: 'vertical', outline: 'none',
            }}
            data-testid="wizard-notes"
          />
        </div>
      </div>
    </div>
  );
}

// Reusable field
function WizardField({ id, label, type = 'text', icon: Icon, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label htmlFor={id} style={{
        display: 'block',
        fontFamily: DA.mono, fontSize: 9.5, fontWeight: 800,
        letterSpacing: '0.28em', textTransform: 'uppercase',
        color: DA.goldDeep, marginBottom: 8,
      }}>
        {label}
      </label>
      <div style={{ position: 'relative' }}>
        {Icon && (
          <Icon size={15} strokeWidth={1.8} style={{
            position: 'absolute', left: 15, top: '50%', transform: 'translateY(-50%)',
            color: DA.inkMuted, pointerEvents: 'none',
          }} />
        )}
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            width: '100%',
            background: DA.paper,
            border: `0.5px solid ${DA.goldSoft}`,
            borderRadius: 12,
            padding: `14px 16px 14px ${Icon ? '40px' : '16px'}`,
            fontFamily: DA.serif, fontSize: 15, fontWeight: 500,
            color: DA.greenDeep, letterSpacing: '-0.005em',
            outline: 'none',
          }}
          data-testid={testid}
        />
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// CONFIRMATION SCREEN
// ═════════════════════════════════════════════════════════════════════
function ConfirmationScreen({ contactName, leadId, restaurantName }) {
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);

  // Compute the same devis ref as the PDF backend (year + last 4 chars of leadId uppercase)
  const devisRef = leadId
    ? `SP-${new Date().getFullYear()}-${leadId.slice(-4).toUpperCase()}`
    : '';

  const whatsappMessage = (() => {
    const nameLine = restaurantName ? ` (${restaurantName})` : '';
    const refLine = devisRef ? ` référence ${devisRef}` : '';
    return `Bonjour Sabril,\n\nJe viens de finaliser mon devis${refLine}${nameLine} sur votre Salon Privé.\n\nJe serais disponible pour un premier échange mardi ou jeudi cette semaine.\n\nMerci,\n${contactName}`;
  })();
  const whatsappUrl = `https://wa.me/${SABRIL_WHATSAPP}?text=${encodeURIComponent(whatsappMessage)}`;

  const handleDownloadDevis = async () => {
    if (!leadId || downloading) return;
    setDownloading(true);
    try {
      const url = `${BACKEND_URL}/api/configurator/devis-pdf/${leadId}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Erreur téléchargement');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `Devis-Sabril-${(contactName || 'client').replace(/\s+/g, '')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
    } catch (e) {
      toast.error('Téléchargement impossible. Vous recevrez le devis par email.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="max-w-xl mx-auto text-center py-6"
      data-testid="wizard-confirmation"
    >
      <div className="flex justify-center mb-6">
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 88, height: 88, borderRadius: 999,
          background: DA.goldMain,
          boxShadow: '0 20px 50px -18px rgba(216,148,64,0.55), inset 0 2px 0 rgba(255,255,255,0.35)',
        }}>
          <Check size={38} strokeWidth={2.4} style={{ color: DA.greenDeep }} />
        </span>
      </div>

      <p style={{
        fontFamily: DA.mono, fontSize: 10, fontWeight: 800,
        letterSpacing: '0.34em', textTransform: 'uppercase',
        color: DA.goldDeep, marginBottom: 10,
      }}>
        Votre candidature est enregistrée
      </p>
      <h2 style={{
        fontFamily: DA.serif, fontSize: 'clamp(30px, 6vw, 44px)', fontWeight: 400,
        letterSpacing: '-0.03em', color: DA.greenDeep, lineHeight: 1.02,
        fontStyle: 'italic', marginBottom: 16,
      }}>
        Merci {contactName}.
      </h2>
      <p style={{
        fontFamily: DA.serif, fontSize: 15, color: DA.inkBody,
        lineHeight: 1.65, letterSpacing: '-0.005em', maxWidth: 460, margin: '0 auto 24px',
      }}>
        Je vais étudier votre dossier personnellement et vous
        recontacterai sous <strong style={{ color: DA.greenDeep }}>72 heures maximum</strong> pour valider votre entrée dans le Salon Privé.
      </p>

      <div style={{
        background: DA.paper,
        border: `0.5px solid ${DA.goldSoft}`,
        borderRadius: 14,
        padding: '18px 20px', marginBottom: 24,
      }}>
        <p style={{
          fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
          letterSpacing: '0.28em', textTransform: 'uppercase',
          color: DA.goldDeep, marginBottom: 8,
        }}>
          Ce qui se passe maintenant
        </p>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, textAlign: 'left' }}>
          {[
            "J'étudie votre dossier personnellement (72h max)",
            'Je vous appelle pour un échange de 30 min sans engagement',
            'Si nous sommes alignés, je vous confirme votre place',
          ].map((s, i) => (
            <li key={i} className="flex items-start gap-2.5" style={{ marginBottom: 8 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 20, height: 20, borderRadius: 999,
                background: DA.greenDeep, color: DA.goldMain,
                fontFamily: DA.mono, fontSize: 10, fontWeight: 800, flexShrink: 0, marginTop: 1,
              }}>
                {i + 1}
              </span>
              <span style={{ fontFamily: DA.serif, fontSize: 13.5, color: DA.inkBody, lineHeight: 1.5 }}>{s}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Devis ref badge */}
      {devisRef && (
        <div className="flex justify-center mb-4">
          <span
            style={{
              fontFamily: DA.mono, fontSize: 10, fontWeight: 800,
              letterSpacing: '0.28em', textTransform: 'uppercase',
              color: DA.goldDeep,
              background: DA.paper,
              border: `0.5px solid ${DA.goldSoft}`,
              borderRadius: 999, padding: '6px 14px',
            }}
            data-testid="wizard-devis-ref-badge"
          >
            Devis · {devisRef}
          </span>
        </div>
      )}

      {/* PRIMARY CTA — WhatsApp direct (accélère le cycle de vente) */}
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full flex items-center justify-center gap-2.5 mb-3 transition-all active:scale-[0.99]"
        style={{
          background: '#25D366',
          color: '#FFFFFF',
          border: `0.5px solid #1EBE5D`,
          borderRadius: 14, padding: '16px 22px',
          fontFamily: DA.serif, fontSize: 15.5, fontWeight: 700, fontStyle: 'italic',
          letterSpacing: '-0.005em', textDecoration: 'none',
          boxShadow: '0 18px 36px -14px rgba(37,211,102,0.55), inset 0 1px 0 rgba(255,255,255,0.35)',
        }}
        data-testid="wizard-whatsapp-cta"
      >
        <MessageCircle size={18} strokeWidth={2.2} style={{ color: '#FFFFFF' }} />
        <span className="flex flex-col items-start" style={{ lineHeight: 1.15 }}>
          <span>Envoyer mon devis à Sabril</span>
          <span style={{
            fontFamily: DA.mono, fontSize: 9, fontWeight: 700,
            letterSpacing: '0.22em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.88)', fontStyle: 'normal', marginTop: 2,
          }}>
            Sur WhatsApp · Réponse sous 4h
          </span>
        </span>
      </a>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
        <button
          type="button"
          onClick={handleDownloadDevis}
          disabled={!leadId || downloading}
          className="inline-flex items-center justify-center gap-2 transition-all active:scale-[0.99]"
          style={{
            background: DA.goldMain, color: DA.greenDeep,
            border: `0.5px solid ${DA.goldDeep}`,
            borderRadius: 12, padding: '14px 22px',
            fontFamily: DA.serif, fontSize: 14, fontWeight: 700, fontStyle: 'italic',
            letterSpacing: '-0.005em',
            cursor: (!leadId || downloading) ? 'not-allowed' : 'pointer',
            opacity: (!leadId || downloading) ? 0.55 : 1,
            boxShadow: (!leadId || downloading) ? 'none' : '0 16px 32px -14px rgba(216,148,64,0.55), inset 0 1px 0 rgba(255,255,255,0.35)',
          }}
          data-testid="wizard-download-devis-btn"
        >
          {downloading ? (
            <><Loader2 size={16} className="animate-spin" /> Téléchargement…</>
          ) : (
            <><Download size={16} strokeWidth={2.2} /> Télécharger mon devis PDF</>
          )}
        </button>

        <button
          type="button"
          onClick={() => navigate('/prsnl')}
          style={{
            background: 'transparent', color: DA.inkMuted,
            border: `0.5px solid ${DA.goldSoft}`,
            borderRadius: 12, padding: '14px 22px',
            fontFamily: DA.serif, fontSize: 13.5, fontWeight: 500, fontStyle: 'italic',
            letterSpacing: '-0.005em', cursor: 'pointer',
          }}
          data-testid="wizard-back-prsnl-btn"
        >
          Retour à l&apos;accueil
        </button>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// MAIN — Wizard controller
// ═════════════════════════════════════════════════════════════════════
export default function ConfiguratorWizardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [unlocked, setUnlocked] = useState(() => isSalonPriveUnlocked());
  const [step, setStep] = useState(0);          // 0..3
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [leadId, setLeadId] = useState(null);

  const [formatKey, setFormatKey] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [chantiers, setChantiers] = useState([]); // list of "n"
  const [contact, setContact] = useState({
    firstName: '', restaurant: '', email: '', phone: '', notes: '',
  });
  const [etape1Done, setEtape1Done] = useState(false); // "J'ai déjà réalisé l'Étape 1 (Sabril Express)"

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'auto' }); }, [step]);

  // Auto-sync chantiers when format changes:
  //  - Hybride → tous les 18 cochés (verrouillés dans l'UI)
  //  - autres formats → on garde la sélection existante (ou vide au premier passage)
  useEffect(() => {
    if (formatKey === 'hybride') {
      setChantiers(CHANTIERS.map((c) => c.n));
    }
  }, [formatKey]);

  // Pré-sélection d'un chantier via query-string (?chantier=07)
  // Utilisé depuis la page de résultats de l'audit gratuit pour amener
  // l'utilisateur directement sur le chantier associé à sa priorité #1.
  // Applique aussi la règle des fondations (01 & 02 obligatoires).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rawParam = params.get('chantier');
    if (!rawParam) return;
    const requested = String(rawParam).padStart(2, '0');
    const isValid = CHANTIERS.some((c) => c.n === requested);
    if (!isValid) return;
    const chantierMeta = CHANTIERS.find((c) => c.n === requested);
    setChantiers((prev) => {
      if (prev.includes(requested) && FOUNDATION_KEYS.every((f) => prev.includes(f))) {
        return prev;
      }
      const next = [...prev];
      if (!next.includes(requested)) next.push(requested);
      // Règle des fondations : si le chantier ajouté n'est pas une fondation, on ajoute 01 & 02
      if (!FOUNDATION_KEYS.includes(requested)) {
        FOUNDATION_KEYS.forEach((f) => { if (!next.includes(f)) next.push(f); });
      }
      return next;
    });
    if (chantierMeta) {
      toast.success(`Chantier ${chantierMeta.n} pré-sélectionné`, {
        description: chantierMeta.title,
      });
    }
  }, [location.search]);

  if (!unlocked) {
    return <SalonPriveGate onUnlocked={() => setUnlocked(true)} />;
  }

  if (submitted) {
    return (
      <PageShell>
        <ConfirmationScreen
          contactName={contact.firstName || 'à vous'}
          restaurantName={contact.restaurant}
          leadId={leadId}
        />
      </PageShell>
    );
  }

  // Step validation
  const canProceed = (() => {
    if (step === 0) return !!formatKey;
    if (step === 1) return !!country && city.trim().length >= 2;
    if (step === 2) return true; // 0 chantier autorisé
    if (step === 3) {
      const c = contact;
      return c.firstName.trim().length >= 2
        && c.restaurant.trim().length >= 2
        && /^\S+@\S+\.\S+$/.test(c.email)
        && c.phone.trim().length >= 6;
    }
    return false;
  })();

  const handleToggleChantier = (n) => {
    setChantiers((prev) => {
      const isSelected = prev.includes(n);
      const isFoundation = FOUNDATION_KEYS.includes(n);

      // Bloquer : essayer de DÉCOCHER une fondation alors qu'il y a d'autres chantiers
      if (isSelected && isFoundation) {
        const others = prev.filter((x) => !FOUNDATION_KEYS.includes(x));
        if (others.length > 0) {
          toast.error("Les chantiers 00 (Coaching) et 01 (ADN) sont les fondations. On ne peut pas les retirer tant qu'il y a d'autres chantiers.");
          return prev;
        }
      }

      // Toggle standard
      let next = isSelected ? prev.filter((x) => x !== n) : [...prev, n];

      // AJOUT d'un chantier hors-fondation → auto-ajouter 01 et 02
      if (!isSelected && !isFoundation) {
        FOUNDATION_KEYS.forEach((f) => { if (!next.includes(f)) next.push(f); });
      }

      // SUPPRESSION du dernier chantier hors-fondation → auto-retirer 01 et 02
      if (isSelected && !isFoundation) {
        const others = next.filter((x) => !FOUNDATION_KEYS.includes(x));
        if (others.length === 0) {
          next = next.filter((x) => !FOUNDATION_KEYS.includes(x));
        }
      }

      return next;
    });
  };
  const handleSelectAll = (all) => {
    setChantiers(all ? CHANTIERS.map((c) => c.n) : []);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const isHybride = formatKey === 'hybride';
      const basePrice = FORMAT_BASE_PRICE[formatKey] || 0;
      const chantiersCost = isHybride ? 0 : chantiers.reduce((s, n) => s + priceOfN(n), 0);
      const totalHT = isHybride ? FORMAT_BASE_PRICE.hybride : basePrice + chantiersCost;
      const payload = {
        format: formatKey,
        format_label: FORMATS.find((f) => f.key === formatKey)?.label,
        country: country,
        chantiers: isHybride ? CHANTIERS.map((c) => c.n) : chantiers,
        full_pack: isHybride || chantiers.length === CHANTIERS.length,
        bundle_key: null,
        total_ht: totalHT,
        contact: {
          firstName: contact.firstName.trim(),
          email: contact.email.trim(),
          phone: contact.phone.trim(),
          restaurant: contact.restaurant.trim(),
          notes: [
            city ? `Ville : ${city}` : null,
            contact.notes.trim() || null,
          ].filter(Boolean).join('\n\n'),
        },
        mode: 'quote',
      };
      const res = await fetch(`${BACKEND_URL}/api/configurator/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur serveur');
      }
      const data = await res.json().catch(() => ({}));
      if (data && data.lead_id) setLeadId(data.lead_id);
      setSubmitted(true);
      try { navigator.vibrate?.(24); } catch (_e) { /* ignore */ }
    } catch (e) {
      toast.error(e.message || "Erreur d'envoi, réessayez ou écrivez-moi sur Instagram.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (!canProceed) return;
    if (step < 3) setStep(step + 1);
    else handleSubmit();
  };
  const handleBack = () => {
    if (step === 0) navigate('/prsnl');
    else setStep(step - 1);
  };

  return (
    <PageShell>
      {/* Top bar */}
      <div className="max-w-5xl mx-auto px-5 md:px-8 pt-6 pb-4 flex items-center justify-between" data-testid="wizard-topbar">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 transition-opacity active:opacity-60"
          style={{
            fontFamily: DA.mono, fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase',
            color: DA.inkMuted, fontWeight: 700,
            background: 'transparent', border: 'none', cursor: 'pointer',
          }}
          data-testid="wizard-back-btn"
        >
          <ChevronLeft size={12} strokeWidth={2.2} />
          {step === 0 ? 'Retour /prsnl' : `Étape ${step}`}
        </button>
        <StepDots step={step} total={4} />
        <p style={{
          fontFamily: DA.mono, fontSize: 10, fontWeight: 700,
          letterSpacing: '0.24em', textTransform: 'uppercase', color: DA.inkMuted,
        }}>
          {step + 1} / 4
        </p>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-5 md:px-8 pb-32 pt-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
          >
            {step === 0 && <StepFormat selected={formatKey} onSelect={setFormatKey} />}
            {step === 1 && <StepLocation country={country} city={city} onCountry={setCountry} onCity={setCity} />}
            {step === 2 && <StepChantiers selectedChantiers={chantiers} onToggle={handleToggleChantier} onSelectAll={handleSelectAll} formatKey={formatKey} />}
            {step === 3 && (
              <StepContact
                contact={contact}
                onChange={(patch) => setContact((prev) => ({ ...prev, ...patch }))}
                formatKey={formatKey}
                country={country}
                city={city}
                selectedChantiers={chantiers}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Sticky footer CTA */}
      <div
        className="fixed bottom-0 inset-x-0 z-40"
        style={{
          background: `linear-gradient(180deg, rgba(247,238,223,0) 0%, ${DA.ivory} 24%, ${DA.ivory} 100%)`,
          padding: '18px 20px 24px',
        }}
      >
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="hidden sm:inline-flex items-center gap-1.5"
            style={{
              fontFamily: DA.mono, fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase',
              color: DA.inkMuted, fontWeight: 700,
              background: 'transparent', border: 'none', cursor: 'pointer', padding: '10px 12px',
            }}
            data-testid="wizard-footer-back"
          >
            <ChevronLeft size={12} strokeWidth={2.2} />
            {step === 0 ? 'Retour' : 'Précédent'}
          </button>

          <button
            type="button"
            onClick={handleNext}
            disabled={!canProceed || submitting}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 transition-all active:scale-[0.99]"
            style={{
              background: (!canProceed || submitting) ? DA.inkFaint : DA.goldMain,
              color: DA.greenDeep,
              border: 'none', borderRadius: 14,
              fontFamily: DA.serif, fontSize: 15, fontWeight: 700, fontStyle: 'italic',
              letterSpacing: '-0.005em', cursor: (!canProceed || submitting) ? 'not-allowed' : 'pointer',
              padding: '16px 28px', minWidth: 220,
              opacity: (!canProceed || submitting) ? 0.6 : 1,
              boxShadow: (!canProceed || submitting) ? 'none' : '0 18px 36px -14px rgba(216,148,64,0.55), inset 0 1px 0 rgba(255,255,255,0.35)',
            }}
            data-testid="wizard-next-btn"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Envoi en cours…
              </>
            ) : step === 3 ? (
              <>
                Ajouter à la liste d&apos;attente
                <ArrowRight size={16} strokeWidth={2.2} />
              </>
            ) : (
              <>
                Suivant
                <ChevronRight size={16} strokeWidth={2.2} />
              </>
            )}
          </button>
        </div>
      </div>
    </PageShell>
  );
}

// ═════════════════════════════════════════════════════════════════════
// PageShell — DA container
// ═════════════════════════════════════════════════════════════════════
function PageShell({ children }) {
  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{
        background: `radial-gradient(ellipse at top, ${DA.paper} 0%, ${DA.ivoryLight} 45%, ${DA.ivory} 100%)`,
      }}
      data-testid="configurator-wizard-page"
    >
      {/* Editorial ornaments */}
      <div style={{ position: 'absolute', top: -30, left: -40, opacity: 0.18, pointerEvents: 'none' }}>
        <MimosaBranch size={240} color={DA.goldMain} />
      </div>
      <div style={{ position: 'absolute', bottom: -40, right: -50, opacity: 0.16, pointerEvents: 'none', transform: 'rotate(180deg)' }}>
        <MimosaBranch size={220} color={DA.goldMain} />
      </div>

      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
}
